import type { FieldGravityStation, FieldObstacle, FieldPoint, FieldSpec } from './field';
import type { TrackPoint } from './types';

// R3 확정 도면: 3500×3500u, 마블 r18u → 엔진 r10px.
export const GRAVITY_BOWLS_SCALE = 10 / 18;
const S = GRAVITY_BOWLS_SCALE;
const G = 240 * S;
const point = (x: number, y: number): FieldPoint => ({ x: x * S, y: y * S });
const rect = (x: number, y: number, w: number, h: number) => ({ ...point(x, y), w: w * S, h: h * S });
const centers = [
  [400, 1750, 340], [1290, 1290, 280], [1750, 400, 340], [2210, 1290, 280],
  [3100, 1750, 340], [2210, 2210, 280], [1750, 3100, 340], [1290, 2210, 280],
  [1750, 1750, 330],
] as const;
const distances = [0];
for (let index = 1; index < centers.length; index += 1) {
  distances.push(distances[index - 1] + Math.hypot(centers[index][0] - centers[index - 1][0], centers[index][1] - centers[index - 1][1]));
}
const totalDistance = distances.at(-1)! + 300;

export const GRAVITY_BOWL_STATIONS: readonly FieldGravityStation[] = centers.map(([x, y, r], index) => {
  const next = centers[index + 1];
  const angle = next ? Math.atan2(next[1] - y, next[0] - x) : Math.PI / 2;
  return { ...point(x, y), r: r * S, progress: distances[index] / totalDistance, gx: Math.cos(angle) * G, gy: Math.sin(angle) * G };
});

const startAngle = Math.atan2(-460, 890);
const startCenter = point(400 + Math.cos(startAngle) * 360, 1750 + Math.sin(startAngle) * 360);

function waveGates(): FieldObstacle[] {
  const angle = Math.atan2(460, -890);
  return [380, 550].flatMap((distance, index) => [-1, 1].map((side): FieldObstacle => {
    const x = 3100 + Math.cos(angle) * distance - Math.sin(angle) * side * 70;
    const y = 1750 + Math.sin(angle) * distance + Math.cos(angle) * side * 70;
    return {
      kind: 'gate', id: `gb-wave-${index}-${side}`, ...rect(x - 8, y - 40, 16, 80), angle,
      schedule: { type: 'cycle', period: 2.4, openSeconds: 1.2, phase: index * .6 + (side > 0 ? 1.2 : 0) },
    };
  }));
}

const bumpers = (prefix: string, coordinates: readonly (readonly [number, number])[], r: number): FieldObstacle[] => coordinates.map(([x, y], index) => ({
  kind: 'bumper', id: `gb-${prefix}-${index}`, ...point(x, y), r: r * S, restitution: .62,
}));

function ramp(): FieldObstacle[] {
  // 원본 램프 끝은 사발 밖이다. 벽을 잇는 장벽 대신 사발 안의 짧은 호로 보정한다.
  const points = [[2280, 2245], [2304, 2286], [2300, 2328], [2272, 2358]] as const;
  return points.slice(1).map((to, index) => {
    const from = points[index];
    return {
      kind: 'bar', id: `gb-ramp-${index}`, ...point((from[0] + to[0]) / 2, (from[1] + to[1]) / 2),
      length: Math.hypot(to[0] - from[0], to[1] - from[1]) * S + 1, thickness: 10 * S,
      angle: Math.atan2(to[1] - from[1], to[0] - from[0]),
    };
  });
}

export const GRAVITY_BOWLS_FIELD: FieldSpec = {
  eventScheduling: 'occupancy',
  shapes: [
    ...centers.map(([x, y, r]) => ({ kind: 'disc' as const, ...point(x, y), r: r * S })),
    ...centers.slice(1).map(([x, y], index) => ({
      kind: 'pipe' as const, points: [point(centers[index][0], centers[index][1]), point(x, y)], width: 220 * S,
    })),
  ],
  holes: [],
  gravity: { x: GRAVITY_BOWL_STATIONS[0].gx, y: GRAVITY_BOWL_STATIONS[0].gy },
  gravityFlow: { stations: GRAVITY_BOWL_STATIONS, blendSeconds: .5, eventType: 'gravity-turn', eventBlendSeconds: .15 },
  damping: .48,
  wallBounce: true,
  // 도면의 66u 피치는 원형 출발장에 100개가 들어가지 않는다. 48u로 원 내부만 채운다.
  spawn: { kind: 'rect', ...rect(100, 1450, 600, 600), pitch: 48 * S, fill: 'center' },
  startGate: { x: startCenter.x - 8 * S, y: startCenter.y - 130 * S, w: 16 * S, h: 260 * S, angle: startAngle },
  // 사발 바닥에 안착한 중심도 놓치지 않도록 원본 스트립을 아래로 연장한다.
  finish: { kind: 'rect', ...rect(1630, 2020, 240, 70) },
  obstacles: [
    ...bumpers('b', [[1200, 1200], [1380, 1230], [1230, 1390], [1390, 1400]], 42),
    { kind: 'disk', id: 'gb-drum-c', ...point(1750, 400), r: 130 * S, hubR: 22 * S, speed: .4, grip: 1.9, eventType: 'drum-reverse' },
    ...[[2100, 1170], [2210, 1170], [2320, 1170], [2155, 1290], [2265, 1290], [2100, 1410], [2210, 1410], [2320, 1410]]
      .map(([x, y], index): FieldObstacle => ({ kind: 'pin', id: `gb-peg-${index}`, ...point(x, y), r: 30 * S })),
    ...waveGates(),
    ...ramp(),
    ...bumpers('g', [[1640, 2990], [1870, 3020], [1720, 3220]], 42),
    { kind: 'disk', id: 'gb-drum-h', ...point(1290, 2210), r: 120 * S, hubR: 22 * S, speed: .4, grip: 1.9, eventType: 'drum-reverse' },
    ...bumpers('arena', [[1640, 1620], [1860, 1620], [1920, 1780], [1750, 1900], [1580, 1780], [1750, 1520]], 40),
  ],
  zones: [
    { kind: 'wait', circle: { ...point(1750, 3100), r: 200 * S }, calm: 2.4, waitForRelease: false, eventType: 'slow-ring-release', eventCalm: 0 },
    // 결승 앞 좌우 펌프: 중력/범퍼는 이벤트에 흔들리지 않는다.
    { kind: 'pump', rect: rect(1450, 1790, 95, 180), ax: 100 * S, ay: 0, period: 1.2 },
    { kind: 'pump', rect: rect(1955, 1790, 95, 180), ax: -100 * S, ay: 0, period: 1.2, phase: .6 },
  ],
  events: [
    { type: 'gravity-turn', duration: 5, window: [12, 20] },
    { type: 'drum-reverse', duration: 5, window: [22, 30] },
    { type: 'slow-ring-release', duration: 4, window: [32, 42] },
  ],
  eventZones: [
    ...GRAVITY_BOWL_STATIONS.slice(0, -2).map((station, index) => ({
      type: 'gravity-turn' as const,
      circle: { x: (station.x + GRAVITY_BOWL_STATIONS[index + 1].x) / 2, y: (station.y + GRAVITY_BOWL_STATIONS[index + 1].y) / 2, r: 70 },
    })),
    { type: 'drum-reverse', circle: { ...point(1750, 400), r: 160 * S } },
    { type: 'drum-reverse', circle: { ...point(1290, 2210), r: 150 * S } },
    { type: 'slow-ring-release', circle: { ...point(1750, 3100), r: 210 * S } },
  ],
  landmarks: [
    { ...point(400, 1410), label: 'A · 출발 사발' },
    { ...point(1290, 1000), label: 'B · 범퍼 광장' },
    { ...point(1750, 60), label: 'C · 회전 드럼' },
    { ...point(2320, 985), label: 'D · 페그 셔플' },
    { ...point(3100, 1410), label: 'E · 파도 게이트' },
    { ...point(2490, 2300), label: 'F · 오버슛 램프' },
    { ...point(1750, 3440), label: 'G · 슬로우 링' },
    { ...point(1080, 2520), label: 'H · 회전 드럼' },
    { ...point(1870, 1370), label: '결승 아레나 · 고정 중력 ↓' },
  ],
  stallSeconds: 3.4,
  rushAt: 72,
  maxSpeed: 700 * S,
  velocityWatchdog: { minSpeed: 8 * S, seconds: 1.2, impulse: 180 * S },
  guideRecovery: true,
  guideRecoveryDistance: 180,
  eventMinGapSeconds: 8,
  eventFinalGuardSeconds: 5,
};

export function gravityBowlsGuidePath(): TrackPoint[] {
  const path: TrackPoint[] = [{ ...point(400, 1750), width: 600 * S }];
  centers.slice(1).forEach(([x, y], index) => {
    const [previousX, previousY] = centers[index];
    for (let step = 1; step <= 4; step += 1) {
      path.push({ ...point(previousX + (x - previousX) * step / 4, previousY + (y - previousY) * step / 4), width: 220 * S });
    }
  });
  path.push({ ...point(1750, 1870), width: 580 * S }, { ...point(1750, 2050), width: 240 * S });
  return path;
}
